import axios from "axios";
import { notifyIfInvalidSession } from "./sessionInvalidation";
import { showErrorToast } from "./swalService";

function inspectSessionFromAxiosError(err, api) {
  void notifyIfInvalidSession({
    status: err?.response?.status,
    data: err?.response?.data,
    url: api || err?.config?.url,
  });
}

function inspectSessionFromAxiosResponse(response, api) {
  void notifyIfInvalidSession({
    status: response?.status,
    data: response?.data,
    url: api || response?.config?.url,
  });
}

async function executePostFetch(api, data, error, axiosExtra = {}) {
  try {
    if (process.env.NODE_ENV === "development" && !api.includes("/searchLds")) {
      console.log("[fetchService] POST:", api);
    }

    const { headers: extraHeaders, ...restExtra } = axiosExtra || {};
    const response = await axios.post(api, data, {
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Accept: "application/json; charset=utf-8",
        ...(extraHeaders && typeof extraHeaders === "object" ? extraHeaders : {}),
      },
      responseType: "json",
      responseEncoding: "utf8",
      withCredentials: true,
      ...restExtra,
    });

    if (process.env.NODE_ENV === "development" && !api.includes("/searchLds")) {
      console.log("[fetchService] POST response:", response.data);
    }
    inspectSessionFromAxiosResponse(response, api);
    return response;
  } catch (err) {
    if (axios.isCancel?.(err) || err?.code === "ERR_CANCELED" || err?.name === "CanceledError") {
      throw err;
    }
    inspectSessionFromAxiosError(err, api);
    let errorMessage = err.message;
    let errorDataFromServer = err.response?.data;
    const httpStatus = err.response?.status;

    if (typeof errorDataFromServer === "string") {
      const trimmed = errorDataFromServer.trim();
      if (trimmed === "") {
        errorDataFromServer = null;
      } else {
        try {
          errorDataFromServer = JSON.parse(trimmed);
        } catch (e) {
          console.warn("[fetchService] Could not parse error response as JSON:", e);
          errorDataFromServer = { message: trimmed, status: "fail" };
        }
      }
    }

    if (
      errorDataFromServer == null ||
      errorDataFromServer === "" ||
      (typeof errorDataFromServer === "string" && errorDataFromServer.trim() === "")
    ) {
      errorDataFromServer = {
        status: "fail",
        message:
          httpStatus === 500
            ? "שגיאת שרת (500) — לא התקבלה תגובת JSON מהשרת."
            : `שגיאת שרת (${httpStatus ?? "?"}).`,
      };
    }

    if (errorDataFromServer?.message) {
      errorMessage = errorDataFromServer.message;
    } else if (errorDataFromServer?.error?.message) {
      errorMessage = errorDataFromServer.error.message;
    } else if (errorDataFromServer?.exception) {
      errorMessage = errorDataFromServer.exception;
    }

    const msgProbe = String(errorMessage || "").toLowerCase();
    const isLdsOrdersListEndpoint =
      typeof api === "string" &&
      (api.includes("/getLDSOrdersByAgentId") ||
        api.includes("/getDynamicOrdersByAgentId"));

    if (
      httpStatus === 403 &&
      isLdsOrdersListEndpoint &&
      (msgProbe.includes("entitlement") ||
        msgProbe.includes("forbidden") ||
        String(errorDataFromServer?.error ?? "").toLowerCase().includes("entitlement"))
    ) {
      if (process.env.NODE_ENV === "development") {
        console.info(
          "[fetchService] entitlement 403 — empty orders list (expected for BASIC)",
          api
        );
      }
      let p = 1;
      let sz = 0;
      try {
        if (data && typeof data === "object") {
          const np = Number(data.page);
          const ns = Number(data.size);
          if (Number.isFinite(np) && np > 0) p = np;
          if (Number.isFinite(ns) && ns >= 0) sz = ns;
        }
      } catch {
        /* ignore payload parse */
      }
      return {
        status: 200,
        statusText: "OK",
        data: { orders: [], total: 0, page: p, page_size: sz },
        headers: err.response?.headers || {},
        config: err.config,
      };
    }

    const isEntitlement403LdsListing =
      httpStatus === 403 &&
      typeof api === "string" &&
      (api.includes("/getLDSOrdersByAgentId") ||
        api.includes("/getDynamicOrdersByAgentId")) &&
      (String(errorMessage || "")
        .toLowerCase()
        .includes("entitlement") ||
        String(errorMessage || "").toLowerCase().includes("forbidden"));

    if (!isEntitlement403LdsListing) {
      console.error("═══════════════════════════════════════════════════════");
      console.error("[fetchService] POST ERROR DETECTED");
      console.error("═══════════════════════════════════════════════════════");
      console.error("🔴 URL:", api);
      console.error("🔴 Method: POST");
      console.error("🔴 Status:", err.response?.status || "No status (Network Error)");
      console.error("🔴 Status Text:", err.response?.statusText || "No status text");
      console.error("🔴 Error Message:", errorMessage);
      if (errorDataFromServer) {
        console.error("🔴 Server Response Data:", JSON.stringify(errorDataFromServer, null, 2));
      }
      if (err.response?.status === 405) {
        console.error("⚠️ 405 Method Not Allowed - השרת לא מאפשר POST ל-URL הזה");
        console.error("⚠️ בדוק בשרת שהרוטה מוגדרת עם methods=[\"POST\"]");
      }
      if (!err.response) {
        console.error("🔴 Network Error Details:", {
          code: err.code,
          message: err.message,
          config: {
            url: err.config?.url,
            method: err.config?.method,
            timeout: err.config?.timeout,
          },
        });
      }
      console.error("═══════════════════════════════════════════════════════");
    }

    if (error && !isEntitlement403LdsListing) {
      showErrorToast(error);
    }
    console.log({ err });
    if (err.response) {
      // New object — avoids mutating shared axios response when requests are coalesced.
      return {
        ...err.response,
        data: errorDataFromServer,
      };
    }
    throw err;
  }
}

export async function performPostFetch(api, data, error, axiosExtra = {}) {
  return executePostFetch(api, data, error, axiosExtra);
}

export async function performPostFetchThrowError(api, data) {
  return axios.post(api, data, {
    withCredentials: true,
  });
}

async function executeGetFetch(api, data = {}, error) {
  try {
    console.log("═══════════════════════════════════════════════════════");
    console.log("[fetchService] -GET REQUEST-");
    console.log("═══════════════════════════════════════════════════════");
    console.log("URL:", api);
    console.log("Method: GET");
    console.log("Params:", data);
    console.log("═══════════════════════════════════════════════════════");

    const res = await axios.get(api, {
      params: data,
      withCredentials: true,
    });
    inspectSessionFromAxiosResponse(res, api);
    const dataRes = res.data;

    console.log("[fetchService] GET response:", dataRes);

    if (typeof dataRes === "string" && dataRes.trim().startsWith("<!doctype")) {
      console.error(`API returned HTML instead of JSON for ${api}. Is API URL configured?`);
      throw new Error(`API returned HTML instead of JSON for ${api}`);
    }
    return dataRes;
  } catch (err) {
    inspectSessionFromAxiosError(err, api);
    let errorMessage = err.message;
    let errorDataFromServer = err.response?.data;

    if (typeof errorDataFromServer === "string") {
      try {
        errorDataFromServer = JSON.parse(errorDataFromServer);
      } catch (e) {
        console.warn("[fetchService] Could not parse error response as JSON:", e);
      }
    }

    if (errorDataFromServer?.message) {
      errorMessage = errorDataFromServer.message;
    } else if (errorDataFromServer?.error?.message) {
      errorMessage = errorDataFromServer.error.message;
    } else if (errorDataFromServer?.exception) {
      errorMessage = errorDataFromServer.exception;
    }

    const status = err.response?.status;
    if (status === 500) {
      console.warn("[fetchService] GET 500:", api, errorMessage);
    } else {
      console.error("═══════════════════════════════════════════════════════");
      console.error("[fetchService] GET ERROR DETECTED");
      console.error("═══════════════════════════════════════════════════════");
      console.error("🔴 URL:", api);
      console.error("🔴 Method: GET");
      console.error("🔴 Status:", status || "No status (Network Error)");
      console.error("🔴 Status Text:", err.response?.statusText || "No status text");
      console.error("🔴 Error Message:", errorMessage);
      if (errorDataFromServer) {
        console.error("🔴 Server Response Data:", JSON.stringify(errorDataFromServer, null, 2));
      }
      if (!err.response) {
        console.error("🔴 Network Error Details:", {
          code: err.code,
          message: err.message,
          config: { url: err.config?.url, method: err.config?.method, timeout: err.config?.timeout },
        });
      }
      console.error("═══════════════════════════════════════════════════════");
    }

    if (error) {
      showErrorToast(error);
    }
    console.log({ err });
    throw err;
  }
}

export async function performGetFetch(api, data = {}, error) {
  return executeGetFetch(api, data, error);
}

async function executePutFetch(api, data, error) {
  try {
    const res = await axios.put(api, data, {
      withCredentials: true,
    });
    inspectSessionFromAxiosResponse(res, api);
    return res;
  } catch (err) {
    inspectSessionFromAxiosError(err, api);
    if (error) {
      showErrorToast(error);
    }
    console.log({ err });
    throw err;
  }
}

export async function performPutFetch(api, data, error) {
  return executePutFetch(api, data, error);
}
